import type { SubtitleCue, SubtitleTrack } from "./room-state";

function timeToSeconds(timeStr: string): number {
  const normalized = timeStr.trim().replace(",", ".");
  const parts = normalized.split(":");
  if (parts.length === 3) {
    return parseFloat(parts[0]) * 3600 + parseFloat(parts[1]) * 60 + parseFloat(parts[2]);
  } else if (parts.length === 2) {
    return parseFloat(parts[0]) * 60 + parseFloat(parts[1]);
  }
  return parseFloat(normalized) || 0;
}

// Parses SRT and WebVTT subtitle files into a normalized SubtitleTrack.
export function parseSubtitleFile(content: string, label = "English"): SubtitleTrack {
  const lines = content.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  const cues: SubtitleCue[] = [];

  let i = 0;
  // Skip WEBVTT header if present
  if (lines[0]?.trim().toUpperCase().startsWith("WEBVTT")) {
    i = 1;
  }

  while (i < lines.length) {
    const line = lines[i]?.trim();
    if (line && line.includes("-->")) {
      const [startStr, endStrWithSettings] = line.split("-->");
      const start = timeToSeconds(startStr);
      const endStr = endStrWithSettings.trim().split(" ")[0];
      const end = timeToSeconds(endStr);

      i++;
      const textLines: string[] = [];
      while (i < lines.length && lines[i].trim() !== "") {
        textLines.push(lines[i].trim());
        i++;
      }

      if (textLines.length > 0 && !isNaN(start) && !isNaN(end)) {
        cues.push({
          id: `cue_${cues.length}`,
          startTime: start,
          endTime: end,
          text: textLines.join("\n"),
        });
      }
    } else {
      i++;
    }
  }

  return {
    id: `sub_${Date.now()}`,
    label,
    language: "en",
    cues,
    offsetMs: 0,
  };
}

export function getActiveCue(track: SubtitleTrack | null, currentTime: number): string | null {
  if (!track || !track.cues || track.cues.length === 0) return null;
  const offsetSec = (track.offsetMs || 0) / 1000;
  const adjustedTime = currentTime - offsetSec;
  const match = track.cues.find(
    (cue) => adjustedTime >= cue.startTime && adjustedTime <= cue.endTime
  );
  return match ? match.text : null;
}
